/**
 * FCM 푸시 토큰 등록 (앱 전역).
 *
 * 기존엔 MainHome 마운트 + 인증 + 브리지 타이밍이 모두 맞아야만 등록됐다.
 * → 신규 유저(온보딩 거쳐 MainHome 안 옴), 화면 이탈, 브리지 늦음 등에서 등록 누락.
 * 그 결과 토큰이 서버에 안 올라가 "다른 유저는 푸시 안 옴" 버그.
 *
 * 여기선:
 *  1) 네이티브가 토큰을 보내면(onFcmTokenReceived) **어느 화면이든** 받아 저장한다(핸들러 상시 ON).
 *  2) 인증되면(앱 시작/로그인) requestFcmToken을 **재시도하며** 보내 토큰을 끌어온다.
 *  3) 토큰을 받았는데 아직 미인증이면 보관해뒀다가 인증되는 순간 서버에 올린다(flush).
 *
 * 네이티브 핸들러: fcm_token_handler.dart (action:'requestFcmToken' 수신 → onFcmTokenReceived 호출).
 */
import { useAuthStore } from '../stores/authStore'
import { isWeb } from './platform'

let pendingToken: string | null = null
let lastSavedToken: string | null = null
let bridgeReady = false
let setupTries = 0

/**
 * 앱 환경 신호로 FlutterChannel 존재 여부를 쓴다.
 * FlutterChannel(JavaScriptChannel)은 문서 시작 시점에 주입돼 페이지 JS보다 먼저 존재한다.
 * 반면 isWeb()이 보는 window.FlutterBridge는 네이티브가 onPageFinished에서 "늦게" 주입하므로,
 * 앱 시작 직후 실행되는 setup/register가 isWeb()로 판별하면 true로 오판해 전부 no-op됐다
 * (→ 토큰이 서버에 영영 등록 안 되던 버그). FlutterChannel은 이 레이스가 없다.
 */
const hasFlutterChannel = (): boolean =>
  typeof window !== 'undefined' &&
  !!(window as any).FlutterChannel &&
  typeof (window as any).FlutterChannel.postMessage === 'function'

const saveToken = async (token: string) => {
  // 같은 토큰을 이미 올렸으면 중복 호출 방지
  if (token === lastSavedToken) return
  if (!useAuthStore.getState().isAuthenticated) {
    pendingToken = token
    return
  }
  try {
    const { userApi } = await import('../api/user')
    await userApi.saveFcmToken(token)
    lastSavedToken = token
    pendingToken = null
  } catch (error) {
    // 실패하면 보관 → 다음 기회(재인증/재요청)에 다시 시도
    pendingToken = token
    console.error('[FCM] 토큰 저장 실패:', error)
  }
}

/** 네이티브 → 웹 토큰 수신 핸들러를 상시 등록 (앱 시작 시 1회, 브리지 늦으면 재시도). */
export const setupFcmBridge = (): void => {
  if (bridgeReady || typeof window === 'undefined') return
  // FlutterChannel이 아직 없으면(웹이거나 앱 초기화 전) 잠깐 후 재시도.
  // 웹 브라우저에선 끝내 안 떠서 몇 번 시도 후 조용히 멈춘다
  // (FlutterBridge를 만들지 않으므로 isWeb() 판별이 오염되지 않음).
  if (!hasFlutterChannel()) {
    if (setupTries++ < 10) setTimeout(setupFcmBridge, 300)
    return
  }
  const bridge = ((window as any).FlutterBridge =
    (window as any).FlutterBridge || {})
  bridge.onFcmTokenReceived = (data: { fcmToken?: string }) => {
    if (data?.fcmToken) {
      void saveToken(data.fcmToken)
    }
  }
  bridgeReady = true
}

/** 네이티브에 토큰을 요청 (브리지가 늦게 뜰 수 있어 몇 번 재시도). */
export const requestFcmToken = (): void => {
  if (typeof window === 'undefined') return
  let tries = 0
  const ask = () => {
    if (hasFlutterChannel()) {
      ;(window as any).FlutterChannel.postMessage(
        JSON.stringify({ action: 'requestFcmToken' })
      )
      return
    }
    if (tries++ < 10) setTimeout(ask, 500)
  }
  ask()
}

/** 인증된 상태에서 호출: 보관 중인 토큰을 올리고, 네이티브에 최신 토큰을 요청. */
export const registerFcm = (): void => {
  if (pendingToken) void saveToken(pendingToken)
  requestFcmToken()
}

/**
 * 로그아웃 시: 이 기기 토큰을 서버에서 제거 (await 권장).
 *
 * 주의: 반드시 **인증 토큰이 살아있을 때**(authApi.logout()·clearAuth() 호출 전) 불러야 한다.
 * 안 그러면 삭제 요청이 401로 실패. 이 기기에 등록된 토큰(lastSavedToken)이 없으면 조용히 건너뛴다
 * (로그인-회수 로직이 커버). 실패해도 throw하지 않아 로그아웃을 막지 않는다.
 */
/**
 * 알림 권한을 **한 번만** 요청한다 (앱, 로그인 상태).
 *
 * 왜 여기 있나
 * ----------
 * 원래 MainHome 안에 있었다. 그래서 홈을 거치지 않는 진입 — 딥링크로 남의
 * 프로필만 보고 나가거나, 사진 공유로 /collect 만 쓰는 경우 — 에서는 **묻지도
 * 않았다.** 토큰 등록(registerFcm)이 이미 전역으로 올라와 있으니 권한 요청도
 * 같은 자리에 둔다.
 *
 * 브리지를 기다린다
 * ---------------
 * window.FlutterBridge 는 페이지 로드가 끝난 뒤(onPageFinished)에 주입된다.
 * 마운트 시점에 한 번만 보고 없으면 끝내던 것이 누락의 원인이었다
 * (2026-10-18 운영 확인: 앱을 쓰는데 토큰이 없는 사용자 61명).
 *
 * 가입 흐름은 건드리지 않는다
 * -------------------------
 * 신규 가입자는 /permissions 에서 **왜 필요한지 설명한 뒤** 묻는다. 여기서
 * 먼저 쏘면 그 설명 없이 팝업만 뜬다. 그래서 브리지를 기다린 뒤 한 박자 쉬고,
 * 그때 서 있는 화면이 가입 흐름이면 비켜준다. 그 사이 /permissions 가 플래그를
 * 세우므로 중복도 안 난다.
 */
const PERM_FLAG = 'notif_perm_requested_v1'

/**
 * 휴대폰 알림 팝업 결과를 서버에 남긴다 — 알림을 허용한 사람이 몇 명인지 세려고.
 *
 * 앱은 결과를 {granted} 로 넘겨주는데, 예전에는 받고도 버렸다.
 * 광고성 푸시 동의와는 다른 값이다(광고 발송은 약관의 마케팅 동의만 본다).
 * 못 보내도 가입·화면 흐름은 막지 않는다.
 */
export const reportNotificationPermission = (
  granted: unknown,
  source: 'signup' | 'home'
): void => {
  if (typeof granted !== 'boolean') return
  import('../api/user')
    .then(({ userApi }) => userApi.reportNotificationPermission(granted, source))
    .catch((error) => console.error('[FCM] 알림 권한 결과 저장 실패(무시):', error))
}

/** 이 화면들에서는 묻지 않는다 — 가입 흐름이 자기 순서에 직접 묻는다. */
const SKIP_PREFIXES = ['/permissions', '/signup', '/register', '/login', '/onboarding']

export const requestNotificationPermissionOnce = (): void => {
  if (typeof window === 'undefined') return

  try {
    if (localStorage.getItem(PERM_FLAG) === '1') return
  } catch {
    /* 저장소를 못 읽어도 아래에서 한 번은 시도한다 */
  }

  const deadline = Date.now() + 8000
  const SETTLE_MS = 1500

  const ask = () => {
    const bridge = (window as any).FlutterBridge
    if (!bridge || typeof bridge.requestNotificationPermission !== 'function') {
      // 옛 네이티브이거나 웹이면 끝내 안 온다. 조용히 포기한다.
      if (Date.now() < deadline) setTimeout(ask, 150)
      return
    }

    // 브리지가 왔다. 가입 흐름이 자리를 잡을 시간을 한 박자 준다.
    setTimeout(() => {
      const path = window.location.pathname
      if (SKIP_PREFIXES.some((p) => path.startsWith(p))) return

      // 플래그는 **부르기 직전에** 세운다. 먼저 세우면 못 물어본 경우에도
      // '물어봤다'로 남아 영영 다시 안 묻는다 — 그게 원래 버그였다.
      try {
        if (localStorage.getItem(PERM_FLAG) === '1') return
        localStorage.setItem(PERM_FLAG, '1')
      } catch {
        /* 저장소 불가 시에도 1회 시도는 진행 */
      }
      // 결과를 받을 곳을 걸어 둔다. 예전엔 아무도 안 받아 결과가 버려졌다.
      // 가입 권한 화면이 자기 것을 걸어 둔 경우엔 그쪽에 맡긴다.
      if (typeof bridge.onNotificationPermissionResult !== 'function') {
        bridge.onNotificationPermissionResult = (data: any) => {
          delete bridge.onNotificationPermissionResult
          reportNotificationPermission(data?.granted, 'home')
        }
      }
      bridge.requestNotificationPermission()
    }, SETTLE_MS)
  }

  ask()
}

export const unregisterFcm = async (): Promise<void> => {
  if (isWeb()) return
  const token = lastSavedToken || pendingToken
  if (!token) return
  try {
    const { userApi } = await import('../api/user')
    await userApi.deleteFcmToken(token)
  } catch (error) {
    console.error('[FCM] 토큰 삭제 실패(무시):', error)
  } finally {
    resetFcmCache()
  }
}

/** 다음 유저가 같은 토큰을 다시 등록할 수 있도록 캐시 초기화. */
export const resetFcmCache = (): void => {
  lastSavedToken = null
  pendingToken = null
}
